import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { followAPI, userAPI, IMG_URL } from '../utils/api';
import { Card, List, Avatar, Space, Typography, Spin, Empty, Button, Tabs, Pagination, Tag, message } from 'antd';
import { UserOutlined, PlusOutlined, MinusOutlined, UserSwitchOutlined } from '@ant-design/icons';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { Text } = Typography;

const Follow = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('following');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [followingMap, setFollowingMap] = useState({});

  // 设置网页标题
  useDocumentTitle('关注管理', {
    prefix: '',
    suffix: ' - rtycn'
  });

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  // 检查是否已关注目标用户
  const [isFollowingTarget, setIsFollowingTarget] = useState(false);

  console.log('Follow component init:', { username, currentUser, isOwner });

  useEffect(() => {
    if (username) {
      fetchUsers();
      // 检查是否已关注目标用户
      if (currentUser && !isOwner) {
        checkFollowingStatus();
      }
    }
  }, [username, activeTab, page]);

  const checkFollowingStatus = async () => {
    if (!currentUser || !username) return;

    try {
      const response = await followAPI.checkFollow(currentUser.username, username);
      setIsFollowingTarget(response.data || false);
    } catch (error) {
      console.error('检查关注状态失败:', error);
      setIsFollowingTarget(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      console.log('fetchUsers: Fetching for username:', username, 'activeTab:', activeTab);

      let response;
      if (activeTab === 'following') {
        response = await followAPI.getFollowing(username, page, pageSize);
      } else {
        response = await followAPI.getFollowers(username, page, pageSize);
      }

      console.log('fetchUsers: Response:', response);
      console.log('fetchUsers: Response data:', response.data);

      if (response.success) {
        const usersList = response.data.list || [];
        setUsers(usersList);
        setTotal(response.data.total || 0);
        console.log('fetchUsers: Users list:', usersList);
        console.log('fetchUsers: Current user:', currentUser);
        console.log('fetchUsers: User list length:', usersList.length);
        // 获取当前登录用户对所有用户的关注状态
        if (currentUser && usersList.length > 0) {
          await fetchFollowingStatus(usersList);
        }
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowingStatus = async (targetUsers) => {
    if (!currentUser) {
      console.log('fetchFollowingStatus: No current user');
      return;
    }

    console.log('fetchFollowingStatus: Fetching for', targetUsers.length, 'users');
    const statusMap = {};
    for (const userFollow of targetUsers) {
      // 根据当前标签页获取目标用户username
      const targetUsername = activeTab === 'following'
        ? userFollow.followingId
        : userFollow.followerId;

      try {
        console.log('Checking follow status:', currentUser.username, '->', targetUsername);
        const response = await followAPI.checkFollow(currentUser.username, targetUsername);
        statusMap[targetUsername] = response.data || false;
        console.log('Follow status for user', targetUsername, ':', statusMap[targetUsername]);
      } catch (error) {
        console.error('Error checking follow status:', error);
        statusMap[targetUsername] = false;
      }
    }
    console.log('Final statusMap:', statusMap);
    setFollowingMap(statusMap);
  };

  const handleFollow = async (targetUsername) => {
    if (!currentUser) {
      return;
    }

    try {
      await followAPI.follow(currentUser.username, targetUsername);
      setFollowingMap({ ...followingMap, [targetUsername]: true });
      // 如果是查看自己的关注列表，刷新列表
      if (isOwner && activeTab === 'following') {
        fetchUsers();
      }
    } catch (error) {
      console.error('关注失败:', error);
    }
  };

  const handleUnfollow = async (targetUsername) => {
    if (!currentUser) {
      return;
    }

    try {
      await followAPI.unfollow(currentUser.username, targetUsername);
      setFollowingMap({ ...followingMap, [targetUsername]: false });
      // 如果取消关注的是目标用户，更新状态
      if (targetUsername === username) {
        setIsFollowingTarget(false);
      }
      // 如果是查看自己的关注列表，刷新列表
      if (isOwner && activeTab === 'following') {
        fetchUsers();
      }
    } catch (error) {
      console.error('取消关注失败:', error);
    }
  };

  const handleFollowTarget = async () => {
    if (!currentUser) {
      message.warning('请先登录');
      return;
    }

    try {
      console.log('开始关注:', { followerId: currentUser.username, followingId: username });
      const result = await followAPI.follow(currentUser.username, username);
      console.log('关注结果:', result);
      setIsFollowingTarget(true);
      message.success('关注成功');
    } catch (error) {
      console.error('关注失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error(error.response?.data?.message || '关注失败');
    }
  };

  const handleUnfollowTarget = async () => {
    if (!currentUser) {
      return;
    }

    try {
      await followAPI.unfollow(currentUser.username, username);
      setIsFollowingTarget(false);
      message.success('取消关注成功');
    } catch (error) {
      console.error('取消关注失败:', error);
      message.error('取消关注失败');
    }
  };

  const UserItem = ({ userFollow }) => {
    // 根据当前标签页决定显示 follower 还是 following 的信息
    const user = activeTab === 'following'
      ? {
          id: userFollow.followingId,
          username: userFollow.followingId,
          nickname: userFollow.followingNickname,
          avatar: userFollow.followingAvatar
        }
      : {
          id: userFollow.followerId,
          username: userFollow.followerId,
          nickname: userFollow.followerNickname,
          avatar: userFollow.followerAvatar
        };

    const isFollowing = followingMap[user.username] || false;
    const canFollow = currentUser && currentUser.username && currentUser.username !== user.username;

    return (
      <List.Item
        actions={
          canFollow
            ? [
                isFollowing ? (
                  <Button
                    danger
                    icon={<MinusOutlined />}
                    onClick={() => handleUnfollow(user.username)}
                  >
                    取消关注
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleFollow(user.username)}
                  >
                    关注
                  </Button>
                )
              ]
            : []
        }
      >
        <List.Item.Meta
          avatar={
            <Link to={`/u/${user.username}`}>
              <Avatar
                size={64}
                src={user.avatar ? `${IMG_URL}${user.avatar}` : undefined}
                icon={<UserOutlined />}
                style={{ cursor: 'pointer' }}
              />
            </Link>
          }
          title={
            <Space>
              <Link to={`/${user.username}/index/`}>
                <Text strong style={{ cursor: 'pointer' }}>{user.username || '未知用户'}</Text>
              </Link>
              {user.nickname && <Text type="secondary">({user.nickname})</Text>}
            </Space>
          }
          description={
            <div>
              <Space direction="vertical" size="small">
                {user.bio && <Text type="secondary">{user.bio}</Text>}
                {user.location && (
                  <Text type="secondary">
                    <Tag>{user.location}</Tag>
                  </Text>
                )}
              </Space>
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', padding: 24 }}>
      <Card
        title={
          <Space>
            <UserSwitchOutlined />
            <span>{isOwner ? '我的关注' : `${username} 的关注`}</span>
            {!isOwner && currentUser && (
              isFollowingTarget ? (
                <Button
                  danger
                  icon={<MinusOutlined />}
                  onClick={handleUnfollowTarget}
                >
                  取消关注
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleFollowTarget}
                >
                  关注
                </Button>
              )
            )}
          </Space>
        }
        tabList={[
          { key: 'following', tab: `关注的用户 (${activeTab === 'following' ? total : '-'})` },
          { key: 'followers', tab: `粉丝 (${activeTab === 'followers' ? total : '-'})` }
        ]}
        activeTabKey={activeTab}
        onTabChange={setActiveTab}
      >
        <Spin spinning={loading}>
          {users.length === 0 ? (
            <Empty description={activeTab === 'following' ? '还没有关注任何人' : '还没有粉丝'} />
          ) : (
            <>
              <List
                itemLayout="horizontal"
                dataSource={users}
                renderItem={(userFollow) => <UserItem userFollow={userFollow} />}
              />
              {total > pageSize && (
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    onChange={setPage}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default Follow;
